import {
  messageSchema,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
} from '@chatwoot/prosemirror-schema';
import * as Sentry from '@sentry/browser';

/**
 * Parse and Serialize the markdown text to remove any extra spaces or new lines
 */
export function cleanSignature(signature) {
  try {
    // remove any horizontal rule tokens
    signature = signature
      .replace(/^( *\* *){3,} *$/gm, '')
      .replace(/^( *- *){3,} *$/gm, '')
      .replace(/^( *_ *){3,} *$/gm, '');

    const nodes = new MessageMarkdownTransformer(messageSchema).parse(
      signature
    );
    return MessageMarkdownSerializer.serialize(nodes);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(e);
    Sentry.captureException(e);
    // The parser can break on some cases
    // for example, Token type `hr` not supported by Markdown parser
    return signature;
  }
}

/**
 * Prefixes the body with the agent's signature, on its own line
 * (`"Signature:\nbody"`), applied only at the send boundary (the composer
 * never stores the signature as part of the editable body).
 *
 * @param {string} body - The message body, without any signature.
 * @param {string} signature - The signature to prefix the body with.
 * @returns {string} - `"${signature}:\n${body}"`, or the body unchanged if
 *   there is no signature, or if it's made up entirely of markdown noise
 *   that `cleanSignature` strips down to an empty string (e.g. a lone `---`).
 */
export function appendSignature(body, signature) {
  const cleanedSignature = signature ? cleanSignature(signature) : '';

  if (!cleanedSignature) {
    return body;
  }

  return `${cleanedSignature}:\n${body}`;
}

/**
 * Single boundary where the agent's signature is concatenated to the
 * outgoing message content, applied only when the per-channel toggle is on.
 * `message` is always the pure body - the signature is never part of it.
 *
 * @param {string} message - The message body, without any signature.
 * @param {string} signature - The signature configured for the agent.
 * @param {boolean} enabled - Whether the signature toggle is on for this send.
 * @returns {string} - The message, prefixed with the signature when enabled.
 */
export function applySignatureIfEnabled(message, signature, enabled) {
  return enabled ? appendSignature(message, signature) : message;
}

/**
 * Extract text from markdown, and remove all images, code blocks, links, headers, bold, italic, lists etc.
 * Links will be converted to text, and not removed.
 *
 * @param {string} markdown - markdown text to be extracted
 * @returns
 */
export function extractTextFromMarkdown(markdown) {
  return markdown
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`.*?`/g, '') // Remove inline code
    .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images before removing links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links but keep the text
    .replace(/#+\s*|[*_-]{1,3}/g, '') // Remove headers, bold, italic, lists etc.
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n') // Trim each line & remove any lines only having spaces
    .replace(/\n{2,}/g, '\n') // Remove multiple consecutive newlines (blank lines)
    .trim(); // Trim any extra space
}

/**
 * Extract plain text from markdown for WhatsApp, converting bold (`**text**`)
 * to WhatsApp's own bold syntax (`*text*`) instead of stripping it - every
 * other mark (italic, code, strikethrough) and node (images) is dropped, same
 * as `extractTextFromMarkdown`. Parses via the same ProseMirror transformer
 * `cleanSignature` already uses, walking the resulting doc's marks, instead
 * of matching the markdown string with a regex - this doesn't depend on
 * which delimiter the serializer happens to use for `strong`/`em`.
 *
 * @param {string} markdown - markdown text to be extracted (e.g. the agent's
 *   configured signature).
 * @returns {string} - plain text with WhatsApp-style bold preserved.
 */
export function extractWhatsAppSignatureText(markdown) {
  if (!markdown) {
    return '';
  }

  try {
    const doc = new MessageMarkdownTransformer(messageSchema).parse(markdown);
    const paragraphs = [];

    doc.forEach(blockNode => {
      let paragraphText = '';

      blockNode.descendants(node => {
        if (node.isText) {
          const isLink = node.marks.some(mark => mark.type.name === 'link');
          const isBold = node.marks.some(mark => mark.type.name === 'strong');
          paragraphText += isBold && !isLink ? `*${node.text}*` : node.text;
        } else if (node.type.name === 'hard_break') {
          // a single `\n` within a paragraph parses as its own `hard_break`
          // node (not text) - without this, it's silently dropped instead of
          // separating the lines it sits between.
          paragraphText += '\n';
        }
        return true;
      });

      if (paragraphText.trim()) {
        paragraphs.push(paragraphText.trim());
      }
    });

    return paragraphs.join('\n');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(e);
    Sentry.captureException(e);
    // The parser can break on some cases, same as cleanSignature above
    return markdown;
  }
}

/**
 * Resolves which transformation to apply to the agent's configured signature
 * before it's sent, based on the composing channel - the pure decision behind
 * `signatureToApply` in `ReplyBox.vue`, extracted so the channel routing
 * itself is testable without mounting the component (see `docs/adr/ADR-0038.md`
 * on why `ReplyBox.vue` can't be `shallowMount`ed today).
 *
 * @param {string} signature - the agent's configured `message_signature`.
 * @param {boolean} isRichTextChannel - true for channels that render markdown
 *   as real formatting (email, web widget) - the signature is left untouched.
 * @param {boolean} isWhatsAppChannel - true for WhatsApp, which supports its
 *   own bold syntax (`*text*`); other plain-text channels fall back to
 *   `extractTextFromMarkdown`.
 * @returns {string}
 */
export function resolveSignatureText(
  signature,
  isRichTextChannel,
  isWhatsAppChannel
) {
  if (isRichTextChannel) {
    return signature;
  }
  return isWhatsAppChannel
    ? extractWhatsAppSignatureText(signature)
    : extractTextFromMarkdown(signature);
}

/**
 * Scrolls the editor view into current cursor position
 *
 * @param {EditorView} view - The Prosemirror EditorView
 *
 */
export const scrollCursorIntoView = view => {
  // Get the current selection's head position (where the cursor is).
  const pos = view.state.selection.head;

  // Get the corresponding DOM node for that position.
  const domAtPos = view.domAtPos(pos);
  const node = domAtPos.node;

  // Scroll the node into view.
  if (node && node.scrollIntoView) {
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

/**
 * Returns a transaction that inserts a node into editor at the given position
 * Has an optional param 'content' to check if the
 *
 * @param {Node} node - The prosemirror node that needs to be inserted into the editor
 * @param {number} from - Position in the editor where the node needs to be inserted
 * @param {number} to - Position in the editor where the node needs to be replaced
 *
 */
export function insertAtCursor(editorView, node, from, to) {
  if (!editorView) {
    return undefined;
  }

  // This is a workaround to prevent inserting content into new line rather than on the exiting line
  // If the node is of type 'doc' and has only one child which is a paragraph,
  // then extract its inline content to be inserted as inline.
  const isWrappedInParagraph =
    node.type.name === 'doc' &&
    node.childCount === 1 &&
    node.firstChild.type.name === 'paragraph';

  if (isWrappedInParagraph) {
    node = node.firstChild.content;
  }

  let tr;
  if (to) {
    tr = editorView.state.tr.replaceWith(from, to, node).insertText(` `);
  } else {
    tr = editorView.state.tr.insert(from, node);
  }
  const state = editorView.state.apply(tr);
  editorView.updateState(state);
  editorView.focus();

  return state;
}

/**
 * Determines the appropriate node and position to insert an image in the editor.
 *
 * Based on the current editor state and the provided image URL, this function finds out the correct node (either
 * a standalone image node or an image wrapped in a paragraph) and its respective position in the editor.
 *
 * 1. If the current node is a paragraph and doesn't contain an image or text, the image is inserted directly into it.
 * 2. If the current node isn't a paragraph or it's a paragraph containing text, the image will be wrapped
 *    in a new paragraph and then inserted.
 * 3. If the current node is a paragraph containing an image, the new image will be inserted directly into it.
 *
 * @param {Object} editorState - The current state of the editor. It provides necessary details like selection, schema, etc.
 * @param {string} fileUrl - The URL of the image to be inserted into the editor.
 * @returns {Object|null} An object containing details about the node to be inserted and its position. It returns null if no image node can be created.
 * @property {Node} node - The ProseMirror node to be inserted (either an image node or a paragraph containing the image).
 * @property {number} pos - The position where the new node should be inserted in the editor.
 */

export const findNodeToInsertImage = (editorState, fileUrl) => {
  const { selection, schema } = editorState;
  const { nodes } = schema;
  const currentNode = selection.$from.node();
  const {
    type: { name: typeName },
    content: { size, content },
  } = currentNode;

  const imageNode = nodes.image.create({ src: fileUrl });

  if (!imageNode) return null;

  const isInParagraph = typeName === 'paragraph';
  const needsNewLine =
    !content.some(n => n.type.name === 'image') && size !== 0 ? 1 : 0;

  return {
    node: isInParagraph ? imageNode : nodes.paragraph.create({}, imageNode),
    pos: selection.from + needsNewLine,
  };
};

/**
 * Set URL with query and size.
 *
 * @param {Object} selectedImageNode - The current selected node.
 * @param {Object} size - The size to set.
 * @param {Object} editorView - The editor view.
 */
export function setURLWithQueryAndSize(selectedImageNode, size, editorView) {
  if (selectedImageNode) {
    // Create and apply the transaction
    const tr = editorView.state.tr.setNodeMarkup(
      editorView.state.selection.from,
      null,
      {
        src: selectedImageNode.src,
        height: size.height,
      }
    );

    if (tr.docChanged) {
      editorView.dispatch(tr);
    }
  }
}
